import { useClerk } from '@clerk/clerk-react'
import { selectOrganization, selectUser } from '@saaga/shared/store/slices/auth.slice'
import { selectSubscriptionInfo } from '@saaga/shared/store/slices/billing.slice'
import { useAppSelector } from '@saaga/shared/store/store'
import { useCallback, useMemo } from 'react'

import type { CreateOrganizationFormData } from '@/components/org-settings/org-settings.interface'

import type { OrganizationData, UserData } from './organization-data-transformers.util'
import {
  transformMembershipsToOrganizations,
  transformOrganizationData,
  transformUserData,
} from './organization-data-transformers.util'
import type { CreateOrganizationDialogState } from './use-create-organization.hook'
import { useCreateOrganization } from './use-create-organization.hook'
import { useOrganizationSelect } from './use-organization-select.hook'

export type { OrganizationData, UserData }
export type { CreateOrganizationDialogState }

export interface AppLayoutOrganizationState {
  organizations: OrganizationData[]
  userData: UserData
  activeOrgData: OrganizationData | null
  subscriptionInfo: ReturnType<typeof selectSubscriptionInfo>
  createOrgDialog: CreateOrganizationDialogState
}

export interface AppLayoutOrganizationHandlers {
  handleSignOut: () => void
  handleOrganizationSelect: (orgId: string) => void
  handleCreateOrganization: () => void
  handleCreateOrgDialogChange: (isOpen: boolean) => void
  handleCreateOrgSubmit: (data: CreateOrganizationFormData) => void
}

export interface UseAppLayoutOrganizationReturn {
  state: AppLayoutOrganizationState
  handlers: AppLayoutOrganizationHandlers
}

export const useAppLayoutOrganization = (): UseAppLayoutOrganizationReturn => {
  const { signOut } = useClerk()
  const user = useAppSelector(selectUser)
  const organization = useAppSelector(selectOrganization)
  const memberships = useAppSelector((state) => state.auth.memberships)
  const subscriptionInfo = useAppSelector(selectSubscriptionInfo)
  const createOrg = useCreateOrganization()
  const handleSignOut = useCallback(() => signOut(), [signOut])
  const handleOrganizationSelect = useOrganizationSelect(memberships)

  const organizations = useMemo(
    () => transformMembershipsToOrganizations(memberships),
    [memberships]
  )
  const userData = useMemo(() => transformUserData(user), [user])
  const activeOrgData = useMemo(() => transformOrganizationData(organization), [organization])

  return {
    state: {
      organizations,
      userData,
      activeOrgData,
      subscriptionInfo,
      createOrgDialog: createOrg.dialogState,
    },
    handlers: {
      handleSignOut,
      handleOrganizationSelect,
      handleCreateOrganization: createOrg.handleOpenDialog,
      handleCreateOrgDialogChange: createOrg.handleDialogChange,
      handleCreateOrgSubmit: createOrg.handleSubmit,
    },
  }
}
